import { FooterQuery } from '@/generated/graphql';
import { Button } from '@/ui/components/button/Button';
import { Container } from '@/ui/components/container/Container';
import clsx from 'clsx';
import { graphql, Link, useStaticQuery } from 'gatsby';
import React from 'react';
import { FooterNewsletterForm } from '../footerNewsletterForm/FooterNewsletterForm';
import { NewWindowIcon } from '@/ui/components/icons/NewWindowIcon';
import * as styles from './Footer.module.scss';
import { routes } from '@/routes/routes';

export const Footer = ({ isContentLimited }: { isContentLimited?: boolean }) => {
  const currentYear = new Date().getFullYear();
  const strapiFooterData = useStaticQuery<FooterQuery>(graphql`
    query Footer {
      strapiFooter {
        footerSections {
          id
          title
          footerLinks {
            id
            title
            url
            shouldOpenInNewTab
          }
        }
        subscribeTitle
        subscribeDesc
        subscribeCTA
        serviceTitle
        serviceDesc
        serviceNumber
        serviceCTA
        serviceCTA2
        serviceCTAtarget
        serviceCTA2target
      }
    }
  `);

  const strapiFooter = strapiFooterData.strapiFooter as any;

  const {
    subscribeTitle = 'Subscribe',
    subscribeDesc = 'The Cochran Wire keeps you plugged into the latest company news, events, and announcements.',
    subscribeCTA = 'Subscribe',
    serviceTitle = '24/7 service',
    serviceDesc = 'Get help around the clock from our services team. For emergency 24/hr service, contact us at',
    serviceNumber = '(206) 367-1900',
    serviceCTA = 'Contact services',
    serviceCTAtarget,
    serviceCTA2,
    serviceCTA2target,
  } = strapiFooter || {};

  const formatSubscribeDesc = (text: string) => {
    if (!text) return '';
    return text.replace(/(cochran wire)/i, '<b>$1</b>');
  };

  // Funkcja do renderowania przycisku z obsługą linków zewnętrznych i wewnętrznych
  const renderServiceButton = (label: string, link?: string) => {
    if (!label || !link) return null;
    if (link.startsWith('http')) {
      return (
        <a href={link} target="_blank" rel="noopener noreferrer" className={styles.contactSection__serviceBtn}>
          <Button variant="white">{label}</Button>
        </a>
      );
    }
    return (
      <Link to={link} className={styles.contactSection__serviceBtn}>
        <Button variant="white">{label}</Button>
      </Link>
    );
  };

  return (
    <div className={styles.footer__outer}>
      <Container>
        <footer className={styles.footer}>
          <div className={styles.contactSection}>
            <div className={styles.footer__section} style={{ display: 'flex', flexDirection: 'column' }}>
              <p className={styles.contactSection__title}>{subscribeTitle}</p>
              <p
                className={styles.contactSection__desc}
                dangerouslySetInnerHTML={{ __html: formatSubscribeDesc(subscribeDesc) }}
              />
              <FooterNewsletterForm buttonText={subscribeCTA} />
            </div>
            <div className={clsx(styles.footer__section, styles.contactSection__service)}>
              <p className={styles.contactSection__serviceTag}>{serviceTitle}</p>
              <p className={styles.contactSection__serviceDesc}>
                {serviceDesc}{' '}
                {serviceNumber ? (
                  <a className={styles.contactSection__phoneNumber} href={`tel:${serviceNumber}`}>
                    ({serviceNumber?.substring(0, 3)}) {serviceNumber?.substring(3, 6)}-{serviceNumber?.substring(6)}
                  </a>
                ) : null}
              </p>
              <div className={styles.contactSection__serviceBtnContainer}>
                {renderServiceButton(serviceCTA, serviceCTAtarget)}
                {renderServiceButton(serviceCTA2, serviceCTA2target)}
              </div>
            </div>
          </div>
          <div className={styles.footer__links}>
            {strapiFooterData.strapiFooter?.footerSections?.map((node) => (
              <ul className={clsx(styles.footer__list, styles.footer__section)} key={node!.id}>
                <li>{node!.title}</li>
                {node!.footerLinks?.map((footerLinkNode) => (
                  <li key={footerLinkNode!.id}>
                    <a
                      href={footerLinkNode!.url!}
                      target={footerLinkNode!.shouldOpenInNewTab ? '_blank' : ''}
                      rel="noreferrer"
                    >
                      {`${footerLinkNode!.title}`}{' '}
                      {footerLinkNode?.shouldOpenInNewTab ? (
                        <NewWindowIcon className={styles.footer__linkIcon} />
                      ) : null}
                    </a>
                  </li>
                ))}
              </ul>
            ))}
          </div>
        </footer>
        <div className={styles.footer__copyrightInfo}>
          {isContentLimited ? null : (
            <Link to={routes.privacyPolicy} className={styles.footer__privacyPolicyText}>
              Privacy Policy
            </Link>
          )}
          <span>© {currentYear}, Cochran Inc. All Rights Reserved.</span>
        </div>
      </Container>
    </div>
  );
};
